import { withTableStore } from './../table/table.store';
import { AxiosError } from 'axios';
import axios from 'utils/axios.config';
import { types, flow, Instance } from 'mobx-state-tree';
import { filterPOSTData, getQueryString } from 'utils/prepareData';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { ErrorData, StatusTypeReq, IResponse } from 'types/common';
import {
  IPointBasicData,
  IPointsCategoryData,
  IPointListData,
  IPointsBasicDataLog,
  IQueryPointsData,
  IQueryPointsBasicLogsData,
  IPointBasicDataReq,
} from './types';
import { DEFAULT_POINT_QUERY } from './constatnts';

const { model, frozen, maybeNull, boolean } = types;

export const PointsStore = model('PointsStore', {
  pointsQuery: frozen<IQueryPointsData>(),
  pointsCategories: frozen<IPointsCategoryData[]>(),
  points: maybeNull(frozen<IPointListData[]>()),
  isPoint: boolean,
  pointBasicData: maybeNull(frozen<IPointBasicData>()),
  pointsBasicLogsQuery: frozen<IQueryPointsBasicLogsData>(),
  pointsLogs: maybeNull(frozen<IPointsBasicDataLog[]>()),
})
  .extend(withAlertStore)
  .extend(withTableStore)
  .actions((self) => ({
    updatePointsQuery(query: IQueryPointsData | null) {
      self.pointsQuery = query ? { ...self.pointsQuery, ...query } : DEFAULT_POINT_QUERY;
    },
    updatePointsBasicLogsQuery(query: IQueryPointsBasicLogsData) {
      self.pointsBasicLogsQuery = { ...self.pointsBasicLogsQuery, ...query };
    },
    setIsPoint(cond: boolean) {
      self.isPoint = cond;
    },
  }))
  .actions((self) => ({
    setPoints(data: IPointListData[]) {
      self.points = data;
    },
    addPoints(data: IPointListData[]) {
      self.points = [...(self.points || []), ...data];
    },
    setPointsCategories(data: IPointsCategoryData[]) {
      self.pointsCategories = data;
    },
    setPointsBasicData(data: IPointBasicData | null) {
      self.pointBasicData = data;
      self.isPoint = true;
      return data;
    },
    setPointsLogsData(data: IPointsBasicDataLog[] | null) {
      self.pointsLogs = data;
    },
    clearData() {
      self.pointBasicData = null;
      self.isPoint = false;
    },
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      const msg = response?.data.message || 'Internal server error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
  }))
  .actions((self) => ({
    getPointsData: flow(function* (query: IQueryPointsData) {
      if (!query.offset) self.updatePointsQuery(query);
      const { category, brand, validationStatus, search, createdByData, isDeleted, ...others } = {
        ...self.pointsQuery,
        ...query,
      };
      const queryStr = getQueryString({
        ...others,
        categoryId: category?.id,
        brandId: brand?.id,
        isValidLocation: validationStatus?.id,
        search: encodeURIComponent(search || ''),
        createdBy: createdByData?.id,
        isDeleted: isDeleted?.id,
      });
      yield axios
        .get(`points?${queryStr}`)
        .then(({ data: { data } }) => {
          if (query.offset) self.addPoints(data);
          else {
            self.setPoints(data);
            self.tableStore.setStartPageIndex(0);
          }
        })
        .catch(self.errorHandling);
    }),
    getPointsCategories: flow(function* () {
      yield axios
        .get(`points/category`)
        .then(({ data: { data } }) => self.setPointsCategories(data))
        .catch(self.errorHandling);
    }),
    changePointStatus: flow(function* ({ id, status }: StatusTypeReq) {
      yield axios
        .patch(`points/status/${id}`, { status })
        .then(() => {
          if (self.points) {
            self.setPoints(self.points.map((item) => (item.id === id ? { ...item, status } : item)));
          }
        })
        .catch(self.errorHandling);
      return id;
    }),
  }))
  .actions((self) => ({
    // POINT BASIC DATA
    getPointBasicData: flow(function* (id: string) {
      yield axios
        .get(`points/basic-data/${id}`)
        .then(({ data: { data } }) => self.setPointsBasicData(data))
        .catch(self.errorHandling);
    }),
    addPoint: flow(function* (data: Omit<IPointBasicData, 'id'>) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { brand, ...filtred } = data;
      const sendData: IPointBasicDataReq = {
        ...filtred,
        categories: data.categories.map(({ id }) => id),
        brandId: data.brand.id,
      };
      const res = yield axios
        .post(`points/basic-data`, sendData)
        .then(({ data: { data } }) => self.setPointsBasicData(data))
        .catch(self.errorHandling);
      return res;
    }),
    editPoint: flow(function* (data: IPointBasicData) {
      const sendData: IPointBasicDataReq = {
        ...data,
        categories: data.categories.map(({ id }) => id),
        brandId: data.brand.id,
      };
      yield axios
        .patch(`points/basic-data/${data.id}`, sendData)
        .then(({ data: { data: newData } }) => {
          self.setPointsBasicData(newData);
          if (self.points) {
            self.setPoints(self.points.map((item) => (item.id === newData.id ? newData : item)));
          }
          return newData;
        })
        .catch(self.errorHandling);
    }),
  }))
  .actions((self) => ({
    // POINT LOGS
    getPointsLogs: flow(function* (query: IQueryPointsBasicLogsData) {
      if (self.pointBasicData) {
        // console.log('query getPointsLogsWithQuery', query);
        const newQuery = { ...self.pointsBasicLogsQuery, ...query };
        if (!(query.limit || query.offset)) self.updatePointsBasicLogsQuery(query);
        const queryStr = Object.entries(
          filterPOSTData({
            ...newQuery,
            typeData: newQuery.typeData?.id,
            // search: strToBase64(newQuery?.search || ''),
            search: encodeURIComponent(newQuery?.search || ''),
          })
        )
          .map(([key, val]) => `${key}=${val}`)
          .join('&');
        // console.log('queryStr', queryStr);
        yield axios
          .get(`points/logs/${self.pointBasicData.id}?${queryStr}`)
          .then(({ data: { data } }) => self.setPointsLogsData(data))
          .catch(self.errorHandling);
      }
    }),
  }));

export type IPointsStore = Instance<typeof PointsStore>;
